import React from 'react';
import { Panel } from 'reactflow';
import { useTheme } from '../../contexts/ThemeContext';
import { WorkflowValidation, formatDuration, formatTokens } from './utils/workflowValidation';

interface WorkflowMetricsPanelProps {
  validation: WorkflowValidation;
  show: boolean;
}

export const WorkflowMetricsPanel: React.FC<WorkflowMetricsPanelProps> = ({ validation, show }) => {
  const { darkMode } = useTheme();

  if (!show) return null;

  const { metrics, errors, warnings } = validation;

  const getComplexityColor = (complexity: number) => {
    if (complexity <= 3) return 'text-green-600';
    if (complexity <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getComplexityBg = (complexity: number) => {
    if (complexity <= 3) return 'bg-green-600';
    if (complexity <= 6) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  return (
    <Panel 
      position="top-right" 
      className={`p-3 rounded-lg shadow-lg border max-w-xs ${
        darkMode 
          ? 'bg-gray-800 border-gray-600 text-white' 
          : 'bg-white border-gray-200 text-gray-900'
      }`}
    >
      <div className="text-sm font-medium mb-2">Workflow Metrics</div>
      
      <div className="space-y-2 text-xs">
        {/* Complexity */}
        <div className="flex items-center justify-between">
          <span>Complexity:</span>
          <div className="flex items-center space-x-1">
            <span className={getComplexityColor(metrics.complexity)}>
              {metrics.complexity}/10
            </span>
            <div className="w-12 bg-gray-200 rounded-full h-1">
              <div 
                className={`h-1 rounded-full ${getComplexityBg(metrics.complexity)}`}
                style={{ width: `${(metrics.complexity / 10) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Duration */}
        <div className="flex items-center justify-between">
          <span>Est. Time:</span>
          <span className="font-mono">{formatDuration(metrics.estimatedDuration)}</span>
        </div>

        {/* Token Budget */}
        <div className="flex items-center justify-between">
          <span>Token Budget:</span>
          <span className="font-mono">{formatTokens(metrics.tokenBudget)}</span>
        </div>

        {/* Parallelizability */}
        <div className="flex items-center justify-between">
          <span>Parallelizable:</span>
          <div className="flex items-center space-x-1">
            <span>{Math.round(metrics.parallelizability * 100)}%</span>
            <div className="w-12 bg-gray-200 rounded-full h-1">
              <div 
                className="bg-blue-600 h-1 rounded-full" 
                style={{ width: `${metrics.parallelizability * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Node counts */}
        <div className="pt-1 border-t border-gray-300 dark:border-gray-600">
          <div className="grid grid-cols-2 gap-1 text-xs">
            <div>Phases: {metrics.nodeCount.phases}</div>
            <div>Tools: {metrics.nodeCount.tools}</div>
            <div>Personas: {metrics.nodeCount.personas}</div>
            <div>Artifacts: {metrics.nodeCount.artifacts}</div>
          </div>
        </div>

        {/* Validation status */}
        {(errors.length > 0 || warnings.length > 0) && (
          <div className="pt-1 border-t border-gray-300 dark:border-gray-600">
            {errors.length > 0 && (
              <div className="text-red-600 text-xs">
                ⚠ {errors.length} error{errors.length > 1 ? 's' : ''}
              </div>
            )}
            {warnings.length > 0 && (
              <div className="text-yellow-600 text-xs">
                ⚠ {warnings.length} warning{warnings.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
};
